// @flow
import React, { useState, Fragment } from 'react';
import { Button } from '@cmsgov/ds-healthcare-gov';
import ExpenseFields from '../ExpenseFields';
import { formatCurrency } from '../../../utilities/currency';
import { frequencyMappedToDisplay } from '../../../common/enums/FrequencyTypes';
import { expenseLabel } from '../../../common/enums/ExpenseTypes';
import { useTranslation } from 'react-i18next';

import type { ExpenseType } from '../../../common/enums/ExpenseTypes';
import type { FrequencyType } from '../../../common/enums/FrequencyTypes';
import type { Expense } from '../../IncomeCalculator';

type Props = {
  key: number,
  id: number,
  editing: boolean,
  expenseType: ExpenseType,
  amount: string,
  expenseFrequency: FrequencyType,
  calculatedTotal: number,
  onUpdateExpenseEntry: (expenseRecord: Expense, id: number) => void,
  onRemoveExpense: (id: number) => void,
};

export const getExpenseSummaryString = (props: any) => {
  const { expenseType, amount, expenseFrequency, t } = props;
  return t('expenseSummaryRow', {
    expenseSource: t(expenseLabel[expenseType]),
    amount: formatCurrency(parseFloat(amount)),
    frequency: t(frequencyMappedToDisplay[expenseFrequency]).toLowerCase(),
  });
};

export const ExpenseRow = (props: Props) => {
  const [editing, setEditing] = useState<boolean>(props.editing);
  const { t } = useTranslation();

  const ariaLabel = (buttonLabel) => `${t(buttonLabel)} ${t(expenseLabel[props.expenseType])}`;

  const handleDoneClick = (expenseRecord: Expense) => {
    props.onUpdateExpenseEntry(expenseRecord, props.id);
    setEditing(false);
  };

  const handleCancelClick = () => {
    setEditing(false);
  };

  let expenseSummary = getExpenseSummaryString({ ...props, t });
  if (expenseSummary) {
    const index = expenseSummary.indexOf(':') + 1;

    const header = expenseSummary.substring(0, index);
    const info = expenseSummary.substring(index);

    expenseSummary = `<strong>${header}</strong>${info}`;
  }

  return (
    <div className="ds-l-md-col--12 ds-u-border-bottom--2">
      {editing ? (
        <ExpenseFields
          expenseType={props.expenseType}
          amount={props.amount}
          expenseFrequency={props.expenseFrequency}
          onCancelAddExpense={handleCancelClick}
          onDoneClick={handleDoneClick}
        />
      ) : (
        <Fragment>
          <div className="ds-l-md-col--12 ds-u-padding--0">
            <div className="ds-u-justify-content--between ds-u-display--flex ds-u-margin-y--1">
              <div dangerouslySetInnerHTML={{ __html: expenseSummary }} />
              <div>{formatCurrency(props.calculatedTotal)}</div>
            </div>
          </div>
          <div className="ds-l-md-col--12 ds-u-margin-bottom--3 ds-u-padding--0">
            <Button
              className="ds-u-margin-right--1"
              size="small"
              onClick={() => setEditing(true)}
              aria-label={ariaLabel('edit')}
            >
              {t('edit')}
            </Button>
            <Button size="small" onClick={() => props.onRemoveExpense(props.id)} aria-label={ariaLabel('remove')}>
              {t('remove')}
            </Button>
          </div>
        </Fragment>
      )}
    </div>
  );
};

export default ExpenseRow;
